import classNames from 'classnames';
import React, { useCallback, useEffect } from 'react';
import { AreaChart } from 'components/AreaChart';
import styles from './styles.module.scss';
import moment from 'moment';
import { ArrowDown, ArrowUp } from './icons';
import { getAddressFormattedCassAddress } from 'helpers/cass';
import { useHistory } from 'react-router-dom';
import { cloneDeep } from 'lodash-es';
import { priceConverter } from 'helpers';

type Props = {
  address: any;
  wrapperClassname?: string;
  valuationData: any;
};

export const AddressDetails: React.FC<Props> = ({ address, wrapperClassname, valuationData }) => {
  const history = useHistory();

  const [addressValuationData, setAddressValuationData] = React.useState<any>(null);
  const [rentalValuationData, setRentalValuationData] = React.useState<any>(null);
  const isNotRecentAddress =
    address?.CreatedDate && moment().diff(moment(address?.CreatedDate), 'minutes') > 5;
  const { address1, address2 } = getAddressFormattedCassAddress(address?.address);

  useEffect(() => {
    if (valuationData) {
      if (valuationData?.addressValuations.valuations?.length > 0) {
        const getValues = (source, value) =>
          cloneDeep(
            valuationData?.addressValuations?.valuations
              ?.filter((obj) => obj?.source === source)
              ?.map((obj) => obj?.[value] || null),
          )?.reverse() ?? [];
        const homeValues = getValues('attom', 'homeValue');
        const homeValuesManual = getValues('manual', 'homeValue');
        const disableAddressDataManual =
          homeValuesManual?.map((value) => value || null).filter(Boolean).length === 0;

        const addressData = [
          {
            name: 'AVM',
            data:
              homeValues?.length > 1
                ? homeValues
                : homeValues?.length
                ? [homeValues[0], homeValues[0]]
                : [],
          },
          ...(disableAddressDataManual
            ? []
            : [
                {
                  name: 'Manual',
                  data:
                    homeValuesManual?.length > 1
                      ? homeValuesManual
                      : homeValuesManual?.length
                      ? [homeValuesManual[0], homeValuesManual[0]]
                      : [],
                },
              ]),
        ];

        const rentalValues = getValues('attom', 'rentalValue');
        const rentalValuesManual = getValues('manual', 'rentalValue');
        const disableRentalDataManual =
          rentalValuesManual?.map((value) => value || null).filter(Boolean).length === 0;

        const rentalData = [
          {
            name: 'AVM',
            data:
              rentalValues?.length > 1
                ? rentalValues
                : rentalValues?.length
                ? [rentalValues[0], rentalValues[0]]
                : [],
          },
          ...(disableRentalDataManual
            ? []
            : [
                {
                  name: 'Manual',
                  data:
                    rentalValuesManual?.length > 1
                      ? rentalValuesManual
                      : rentalValuesManual?.length
                      ? [rentalValuesManual[0], rentalValuesManual[0]]
                      : [],
                },
              ]),
        ];
        setAddressValuationData(addressData);
        setRentalValuationData(rentalData);
      }
    }
  }, [valuationData]);

  const categories = cloneDeep(valuationData?.addressValuations.valuations)
    ?.reverse()
    ?.map((obj) => {
      const { month, year } = obj;
      return (
        moment()
          .month(month - 1)
          .format('MMM') +
        ' ' +
        year
      );
    });

  const AreaChartOptions = (series) => ({
    tooltip: {
      enabled: true,
      custom: ({ dataPointIndex, w }) => {
        let tooltip = '';

        series.forEach((s, index) => {
          const point = w.globals.initialSeries[index].data[dataPointIndex];
          const value =
            point || point === 0
              ? `$${Number(point).toLocaleString('en-US', {
                  currency: 'USD',
                  maximumFractionDigits: 0,
                })}`
              : '-';
          const name = s.name;
          const color = w.globals.colors[index];

          tooltip += `
            ${
              index === 0
                ? `<p style="color: #262626;
                margin-bottom: 6px;
                font-family: Inter;
                font-size: 14px;
                font-style: normal;
                font-weight: 500;
                line-height: 16px;
                letter-spacing: -0.18px;">
                ${categories?.[dataPointIndex] || categories?.[dataPointIndex - 1]}
            </p>`
                : ''
            }
              <p style="color: #262626;
                margin-bottom: 4px;
                font-family: Inter;
                font-size: 20px;
                font-style: normal;
                font-weight: 700;
                line-height: 20px;
                letter-spacing: -0.18px;">
                ${value}
                
              </p>
              <div style="margin-top: 8px; ${
                series.length > 1 ? 'margin-bottom: 14px;' : ''
              } display: flex; align-items: center; gap: 8px">
                <div style="padding-top: 3px; width: 10px; height: 10px; border: 2px solid ${color}; border-radius: 3px;"></div>
                <p style="color: #676767;
                  margin: 0;
                  font-family: Inter;
                  font-size: 13px;
                  font-style: normal;
                  font-weight: 400;
                  line-height: 16px;
                  letter-spacing: -0.18px;">
                  ${name}
                </p>
         </div>     
              `;
        });

        return `<div style="background: #fff; padding: 16px; border-radius: 16px; min-width: 140px">
                  ${tooltip}
                </div>`;
      },
    },
    xaxis: {
      labels: {
        show: false,
      },
    },
  });

  const openAddress = useCallback(() => {
    history.push('/intel', {
      address1Param: address1,
      address2Param: address2,
      addressIdParam: address?.Id,
    });
  }, [address?.address]);

  const renderPlaceHolder = () => {
    return isNotRecentAddress ? (
      <div>-</div>
    ) : (
      <div>
        <div className={styles.placeholderContainer}>
          <div className={classNames(styles.placeholderText)}>Calculating...</div>
          <div className={styles.whiteOverlay} />
        </div>
        <div className={styles.placeholderLine} />
      </div>
    );
  };

  return (
    <div className={classNames(styles.addressWrapper, wrapperClassname)}>
      <div className={styles.header} onClick={openAddress}>
        <div className={styles.title}>{address1}</div>
        <div className={styles.subtitle}>{address2}</div>
      </div>
      <div className={styles.content}>
        <div className={styles.plotContainer}>
          <>
            <div className={styles.title}>Estimated Home Value</div>
            <div className={classNames(styles.subtitle, styles.purple)}>
              {valuationData?.addressValuations?.homeValue &&
              valuationData?.addressValuations?.currentManualHomeValue
                ? priceConverter(
                    Number(
                      Math.min(
                        Number(valuationData?.addressValuations?.homeValue),
                        Number(valuationData?.addressValuations?.currentManualHomeValue),
                      ),
                    ),
                  ) +
                  ' - ' +
                  priceConverter(
                    Number(
                      Math.max(
                        Number(valuationData?.addressValuations?.homeValue),
                        Number(valuationData?.addressValuations?.currentManualHomeValue),
                      ),
                    ),
                  )
                : valuationData?.addressValuations?.homeValue
                ? '$' +
                  Number(valuationData?.addressValuations?.homeValue).toLocaleString('en-US', {
                    currency: 'USD',
                    maximumFractionDigits: 0,
                  })
                : renderPlaceHolder()}

              {valuationData?.addressValuations?.homeValue &&
              valuationData?.addressValuations?.homeValue !== 0 &&
              addressValuationData?.length >= 2 ? (
                <span
                  className={
                    !valuationData?.addressValuations.homeValueUp &&
                    valuationData?.addressValuations.homePercent
                      ? styles.decrease
                      : styles.increase
                  }
                >
                  <div>
                    {!valuationData?.addressValuations?.homeValueUp &&
                    valuationData?.addressValuations?.homePercent ? (
                      <ArrowDown />
                    ) : (
                      <ArrowUp />
                    )}
                    {valuationData?.addressValuations?.homePercent
                      ? parseFloat(valuationData?.addressValuations?.homePercent).toFixed(2)
                      : 0}
                    %
                  </div>
                </span>
              ) : null}
            </div>

            {addressValuationData && (
              <AreaChart
                curve="smooth"
                height={70}
                chartSeries={addressValuationData}
                options={AreaChartOptions(addressValuationData)}
                colors={['#827cce', '#6cd5f1']}
              />
            )}
          </>
        </div>
        <div className={styles.plotContainer}>
          <>
            <div className={styles.title}>Estimated Rental Value</div>
            <div className={classNames(styles.subtitle, styles.aqua)}>
              {valuationData?.addressValuations?.rentalValue &&
              valuationData?.addressValuations?.currentManualRentalValue
                ? priceConverter(
                    Number(
                      Math.min(
                        Number(valuationData?.addressValuations?.rentalValue),
                        Number(valuationData?.addressValuations?.currentManualRentalValue),
                      ),
                    ),
                  ) +
                  ' - ' +
                  priceConverter(
                    Number(
                      Math.max(
                        Number(valuationData?.addressValuations?.rentalValue),
                        Number(valuationData?.addressValuations?.currentManualRentalValue),
                      ),
                    ),
                  )
                : valuationData?.addressValuations?.rentalValue
                ? '$' +
                  Number(valuationData?.addressValuations?.rentalValue).toLocaleString('en-US', {
                    currency: 'USD',
                    maximumFractionDigits: 0,
                  })
                : renderPlaceHolder()}

              {valuationData?.addressValuations?.rentalValue &&
              valuationData?.addressValuations?.rentalValue !== 0 &&
              rentalValuationData?.length >= 2 ? (
                <span
                  className={
                    !valuationData?.addressValuations?.rentalValueUp &&
                    valuationData?.addressValuations?.rentalPercent
                      ? styles.decrease
                      : styles.increase
                  }
                >
                  <div>
                    {!valuationData?.addressValuations.rentalValueUp &&
                    valuationData?.addressValuations.rentalPercent ? (
                      <ArrowDown />
                    ) : (
                      <ArrowUp />
                    )}
                    {valuationData?.addressValuations?.rentalPercent
                      ? parseFloat(valuationData?.addressValuations?.rentalPercent).toFixed(2)
                      : 0}
                    %
                  </div>
                </span>
              ) : null}
            </div>

            {rentalValuationData && (
              <AreaChart
                curve="smooth"
                height={70}
                chartSeries={rentalValuationData}
                options={AreaChartOptions(rentalValuationData)}
                colors={['#6cd5f1', '#827cce']}
              />
            )}
          </>
        </div>
      </div>

      <div className={styles.activityWrapper}>
        <div className={styles.activityCard}>
          <div className={styles.activityCardTitle}>Neighborhood Activity</div>
          <div className={classNames(styles.activityCardContent, styles.orangeTitle)}>
            {valuationData?.neighborhoodActivities?.total !== 0 ||
            valuationData?.addressValuations?.homeValue ||
            valuationData?.addressValuations?.rentalValue ? (
              valuationData?.neighborhoodActivities?.total
            ) : (
              <div className={styles.dots}>...</div>
            )}
          </div>
        </div>
        <div className={styles.activityCard}>
          <div className={styles.activityCardTitle}>Reminders</div>
          <div className={classNames(styles.activityCardContent, styles.redTitle)}>
            {valuationData?.reminders?.length || <div className={styles.dots}>...</div>}
          </div>
        </div>
        <div className={styles.activityCard}>
          <div className={styles.activityCardTitle}>New Intel</div>
          <div className={classNames(styles.activityCardContent, styles.greenTitle)}>
            <div className={styles.dots}>...</div>
          </div>
        </div>
      </div>
    </div>
  );
};
